import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useDebouncedTerm } from './debounce.js';

/**
 * O estado de uma lista — página, tamanho, busca e filtros — NA URL.
 *
 * ## Por que na URL, e não em `useState`
 *
 * Pedido de 19/10/2026: quem abre uma ficha a partir da página 3 de uma lista e
 * volta tem de cair na página 3, com a mesma busca e os mesmos filtros. Em
 * `useState` o estado morre com a tela: o voltar do navegador remonta a lista
 * do zero, na página 1. Na URL, o histórico do navegador É o estado — e a URL
 * copiada para um colega abre a mesma página.
 *
 * É o padrão que `/sales/portfolio` e `/sales/statement` já usavam
 * (`routes.tsx`), agora com nome e num lugar só.
 *
 * ## `replace` para digitar, `push` para andar
 *
 * Mudar a busca ou um filtro REESCREVE a entrada do histórico e volta para a
 * página 1: cada tecla não pode virar um "voltar". Mudar de página ou de tamanho
 * EMPILHA, e é isso que deixa o voltar refazer o caminho página a página.
 */

/** Os tamanhos que o seletor oferece — os mesmos de `Pagination` (`@evogard/ui`). */
export const PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

/** Dez por página em toda tabela do ERP — decisão de 19/10/2026. */
export const DEFAULT_PER_PAGE = 10;

/** A página que vem da URL: inteiro a partir de 1; qualquer outra coisa é a primeira. */
export const pageParam = z.coerce.number().int().min(1).catch(1);

/** O tamanho que vem da URL: um dos oferecidos; fora deles, o padrão. */
export const perPageParam = z.coerce
  .number()
  .int()
  .refine((n) => (PER_PAGE_OPTIONS as readonly number[]).includes(n))
  .catch(DEFAULT_PER_PAGE);

/** Um filtro de texto curto: ausente, vazio ou torto é "sem filtro". */
export const textParam = (max = 64) => z.string().max(max).optional().catch(undefined);

/** O `validateSearch` de uma lista: página, tamanho, busca e os filtros dela. */
export function listSearch<S extends z.ZodRawShape>(filters: S) {
  return z.object({
    page: pageParam,
    perPage: perPageParam,
    q: textParam(),
    ...filters,
  });
}

/** A página de cada tabela de uma ficha, cada uma por um nome (`?alteracoes=2`). */
export function tablePages<const K extends string>(names: readonly K[]) {
  return z.object(
    Object.fromEntries(
      names.map((n) => [n, pageParam.optional().catch(undefined)]),
    ) as Record<K, z.ZodCatch<z.ZodOptional<typeof pageParam>>>,
  );
}

export interface ListState {
  page: number;
  perPage: number;
  q?: string | undefined;
}

/**
 * A última URL de cada lista nesta aba — o que o link "‹ Lista" da ficha usa.
 *
 * Memória do processo, e não `sessionStorage`: a lista que a pessoa viu é desta
 * aba, e uma ficha aberta direto, sem lista antes, volta para a lista limpa.
 */
const lastSearch = new Map<string, Record<string, unknown>>();

/** A busca com que a lista `path` estava da última vez — vazia se nunca foi aberta. */
export function backSearch(path: string): Record<string, unknown> {
  return lastSearch.get(path) ?? {};
}

/**
 * Os setters de uma lista. `search` é o que a rota já validou (`useSearch` da
 * tela); este hook só sabe escrever de volta, e lembrar onde a lista estava.
 */
export function useListSearch<T extends ListState>(search: T) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    lastSearch.set(pathname, search as unknown as Record<string, unknown>);
  }, [pathname, search]);

  const write = (patch: Partial<T>, mode: 'andar' | 'filtrar') =>
    void navigate({
      to: '.',
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        ...patch,
        ...(mode === 'filtrar' ? { page: 1 } : {}),
      })) as never,
      replace: mode === 'filtrar',
    });

  return {
    page: search.page,
    perPage: search.perPage,
    setPage: (page: number) => write({ page } as Partial<T>, 'andar'),
    /* trocar a quantidade volta para a primeira página — ver `Pagination` */
    setPerPage: (perPage: number) => write({ perPage, page: 1 } as Partial<T>, 'andar'),
    /** Filtros e busca: reescrevem a entrada e voltam para a página 1. */
    setFilters: (patch: Partial<T>) => write(patch, 'filtrar'),
  };
}

/**
 * Leva a URL para a última página que existe quando a pedida passou do fim —
 * um link antigo, ou a lista que encolheu. `replace`: a página que não existia
 * não deve ficar no histórico para o voltar cair nela de novo.
 *
 * Só depois de a resposta chegar (`total` definido) e só com linhas: lista
 * vazia tem uma página, e a página 1 já é ela.
 */
export function useClampedPage(
  page: number,
  perPage: number,
  total: number | undefined,
): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (total === undefined || total === 0) return;
    const last = Math.max(1, Math.ceil(total / perPage));
    if (page > last) {
      void navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => ({ ...prev, page: last })) as never,
        replace: true,
      });
    }
  }, [page, perPage, total, navigate]);
}

/**
 * O campo de busca de uma lista: o texto do campo fica local (cada tecla não
 * vai para a URL), e o termo, depois do atraso, vai.
 *
 * Nasce do que a URL tem, e por isso o voltar devolve a busca no campo. Quando
 * a URL muda por fora (o voltar do navegador), o campo acompanha.
 */
export function useSearchTerm(
  urlTerm: string | undefined,
  write: (q: string | undefined) => void,
): { text: string; setText: (text: string) => void } {
  const [text, setText] = useState(urlTerm ?? '');
  const term = useDebouncedTerm(text);
  const written = useRef(urlTerm ?? '');

  /* a URL mudou por fora — voltar, avançar, link —: o campo segue a URL */
  useEffect(() => {
    if ((urlTerm ?? '') !== written.current) {
      written.current = urlTerm ?? '';
      setText(urlTerm ?? '');
    }
  }, [urlTerm]);

  /* o termo assentou e é outro: vai para a URL */
  useEffect(() => {
    if (term !== written.current) {
      written.current = term;
      write(term === '' ? undefined : term);
    }
    // `write` muda a cada render e não é o gatilho; o termo é
  }, [term]);

  return { text, setText };
}

/**
 * A página de UMA tabela da ficha, pelo nome dela na URL (`?boletos=3`).
 *
 * `push`: andar de página numa tabela da ficha é andar, e o voltar desfaz.
 */
export function useTablePage(
  name: string,
  value: number | undefined,
): { page: number; onPageChange: (page: number) => void } {
  const navigate = useNavigate();
  return {
    page: value ?? 1,
    onPageChange: (page) =>
      void navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          [name]: page === 1 ? undefined : page,
        })) as never,
      }),
  };
}
